/**
 * Serialization is written out by hand, field by field. No generic reviver,
 * no reflection: a change to GameState should be a compile error here rather
 * than a silently NaN'd Decimal in someone's save.
 *
 * Decimal values go to disk as strings via .toString() and come back via
 * new Decimal(str).
 */

import Decimal from 'break_infinity.js';
import {
  createNewState,
  type GameSettings,
  type GameState,
  type NumberFormat,
} from '../sim/state.ts';
import { GENERATOR_COUNT } from '../data/generators.ts';
import {
  CLOCK_DEFAULT,
  DEFAULT_AUTOMELT_THRESHOLD,
  SAVE_BROKEN_KEY,
  SAVE_KEY,
} from '../data/balance.ts';
import { CURRENT_VERSION, migrate } from './migrate.ts';

// ---------------------------------------------------------------------------
// Coercion helpers - an imported or hand-edited save must never crash the game
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function dec(v: unknown, fallback: string): Decimal {
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Decimal(v);
    if (Number.isFinite(d.mantissa) && Number.isFinite(d.exponent)) return d;
  }
  return new Decimal(fallback);
}

function numberRecord(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v !== null && typeof v === 'object') {
    for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
      if (typeof raw === 'number' && Number.isFinite(raw)) out[k] = raw;
    }
  }
  return out;
}

function boolRecord(v: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (v !== null && typeof v === 'object') {
    for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
      if (typeof raw === 'boolean') out[k] = raw;
    }
  }
  return out;
}

/** Owned counts, normalised to exactly GENERATOR_COUNT non-negative integers. */
function generatorCounts(v: unknown): number[] {
  const out = new Array<number>(GENERATOR_COUNT).fill(0);
  if (Array.isArray(v)) {
    for (let i = 0; i < GENERATOR_COUNT; i++) {
      const raw = v[i];
      if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
        out[i] = Math.floor(raw);
      }
    }
  }
  return out;
}

function settings(v: unknown): GameSettings {
  const s = (v !== null && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const format: NumberFormat = s.numberFormat === 'scientific' ? 'scientific' : 'standard';
  return {
    numberFormat: format,
    reducedMotion: typeof s.reducedMotion === 'boolean' ? s.reducedMotion : null,
    autoMeltdownEnabled: bool(s.autoMeltdownEnabled, false),
    autoMeltdownThreshold: num(s.autoMeltdownThreshold, DEFAULT_AUTOMELT_THRESHOLD),
    autoBuy: boolRecord(s.autoBuy),
  };
}

// ---------------------------------------------------------------------------
// Serialize / deserialize
// ---------------------------------------------------------------------------

export function serialize(state: GameState): string {
  return JSON.stringify({
    version: CURRENT_VERSION,

    thoughts: state.thoughts.toString(),
    thoughtsThisRun: state.thoughtsThisRun.toString(),
    generators: state.generators,
    clock: state.clock,
    heat: state.heat,
    peakHeatThisRun: state.peakHeatThisRun,
    ventCooldown: state.ventCooldown,
    runTimeSec: state.runTimeSec,

    greyMatter: state.greyMatter.toString(),
    ideaRemainder: state.ideaRemainder.toString(),
    skills: state.skills,

    stats: {
      lifetimeThoughts: state.stats.lifetimeThoughts.toString(),
      meltdowns: state.stats.meltdowns,
      bestRunThoughts: state.stats.bestRunThoughts.toString(),
      hottestSurvived: state.stats.hottestSurvived,
      fastestMeltdownSec: state.stats.fastestMeltdownSec,
      totalPlaytimeSec: state.stats.totalPlaytimeSec,
      firstPlayedAt: state.stats.firstPlayedAt,
    },
    unlocks: state.unlocks,
    settings: state.settings,

    entities: state.entities,

    lastSavedAt: state.lastSavedAt,
  });
}

/** Throws if the string is unusable. Callers treat a throw as "corrupt save". */
export function deserialize(raw: string, now: number): GameState {
  const s = migrate(JSON.parse(raw));
  const stats = (s.stats !== null && typeof s.stats === 'object' ? s.stats : {}) as Record<
    string,
    unknown
  >;

  return {
    version: CURRENT_VERSION,

    thoughts: dec(s.thoughts, '0'),
    thoughtsThisRun: dec(s.thoughtsThisRun, '0'),
    generators: generatorCounts(s.generators),
    clock: num(s.clock, CLOCK_DEFAULT),
    heat: num(s.heat, 0),
    peakHeatThisRun: num(s.peakHeatThisRun, num(s.heat, 0)),
    ventCooldown: num(s.ventCooldown, 0),
    runTimeSec: num(s.runTimeSec, 0),

    greyMatter: dec(s.greyMatter, '0'),
    // Absent in saves written before partial ideas carried over.
    ideaRemainder: dec(s.ideaRemainder, '0'),
    skills: numberRecord(s.skills),

    stats: {
      lifetimeThoughts: dec(stats.lifetimeThoughts, '0'),
      meltdowns: num(stats.meltdowns, 0),
      bestRunThoughts: dec(stats.bestRunThoughts, '0'),
      hottestSurvived: num(stats.hottestSurvived, 0),
      fastestMeltdownSec: num(stats.fastestMeltdownSec, 0),
      totalPlaytimeSec: num(stats.totalPlaytimeSec, 0),
      firstPlayedAt: num(stats.firstPlayedAt, now),
    },
    unlocks: boolRecord(s.unlocks),
    settings: settings(s.settings),

    entities: [],

    lastSavedAt: num(s.lastSavedAt, now),
  };
}

// ---------------------------------------------------------------------------
// localStorage
// ---------------------------------------------------------------------------

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Private mode / disabled storage throws on access.
    return null;
  }
}

export type LoadResult =
  | { kind: 'new'; state: GameState }
  | { kind: 'loaded'; state: GameState }
  | { kind: 'broken'; state: GameState; error: string };

export function loadGame(now: number): LoadResult {
  const store = storage();
  const raw = store === null ? null : store.getItem(SAVE_KEY);
  if (raw === null || raw === '') {
    return { kind: 'new', state: createNewState(now) };
  }

  try {
    return { kind: 'loaded', state: deserialize(raw, now) };
  } catch (err) {
    // Never silently wipe. Stash the raw string so it can be recovered.
    try {
      store?.setItem(SAVE_BROKEN_KEY, raw);
    } catch {
      /* storage may be full; the toast still tells the player what happened */
    }
    return {
      kind: 'broken',
      state: createNewState(now),
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Stamps lastSavedAt and writes. Returns false if storage refused. */
export function saveGame(state: GameState, now: number): boolean {
  state.lastSavedAt = now;
  const store = storage();
  if (store === null) return false;
  try {
    store.setItem(SAVE_KEY, serialize(state));
    return true;
  } catch {
    return false;
  }
}
